import { useState, useContext, useEffect } from 'react';
import Employee from '../types/api/Employee';
import { AppContext } from '../context/appContext';
import dayjs, { Dayjs } from 'dayjs';
import { DateCalendar, DatePicker, PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import { Button, Checkbox, useTheme } from '@mui/material';
import { addLeaveDays, getLeaveDaysOfEmployee, removeLeaveDays } from '../api/restaurant';
import LeaveDay from '../types/api/LeaveDays';
import { getWeekNumber } from '../tools/utils';

interface EmployeeLeaveDayProps {
    baseEmployee: Employee,
    closeDialog: ()=>void
}

function ServerDay(props: PickersDayProps<Dayjs> & { highlightedDays?: number[], employeeId?: number, setEmployeeLeaveDays?: (leaveDays: LeaveDay[]) => void }) {
    const theme = useTheme();
    const { highlightedDays = [], day, outsideCurrentMonth, setEmployeeLeaveDays, employeeId, ...other } = props;
    const appContext = useContext(AppContext);

    const isSelected = !props.outsideCurrentMonth && highlightedDays.indexOf(props.day.date()) >= 0;

    const removeLeaveDay = async () => {
        if(isSelected){
            const res = await removeLeaveDays([{employeeId: employeeId, dayNumber: day.date(), month: day.month(), year: day.year() } as LeaveDay])
            if (res && res.code === 200 && res.data && setEmployeeLeaveDays) {
                setEmployeeLeaveDays(res.data);
                appContext.showSnackbar("Congé supprimé avec succès.", "success")
            }else{
                appContext.showSnackbar("Erreur lors de la suppression du congé.", "error")
            }
        }
    }


    return (
        <div style={isSelected ? { backgroundColor: theme.palette.success.main } : undefined} onClick={removeLeaveDay}>
            <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
        </div>
    );
}

export function EmployeeLeaveDay(EmployeeLeaveDayProps: EmployeeLeaveDayProps) {
    const appContext = useContext(AppContext);

    const [isLongLeave, setIsLongLeave] = useState<boolean>(false)
    const [startDate, setStartDate] = useState(dayjs())
    const [endDate, setEndDate] = useState(startDate.add(1, 'day')) 
    const [employeeLeaveDays, setEmployeeLeaveDays] = useState<LeaveDay[]>([])

    const [highlightedDays, setHighlightedDays] = useState<number[]>([])
    const [currentMonth, setCurrentMonth] = useState(dayjs().month()) 

    useEffect(() => {
        async function fetchLeaveDays() {
            const res = await getLeaveDaysOfEmployee(EmployeeLeaveDayProps.baseEmployee)
            if (res && res.code === 200 && res.data) {
                setEmployeeLeaveDays(res.data);
            }
        }
        fetchLeaveDays();
    }, [EmployeeLeaveDayProps.baseEmployee])

    useEffect(() => {
        const monthlyLeaveDays = employeeLeaveDays.filter((leaveDay) => {
            return currentMonth === leaveDay.month
        }).map((leaveDay) => {
            return leaveDay.dayNumber
        })
        setHighlightedDays(monthlyLeaveDays)
    }, [currentMonth, employeeLeaveDays])

    const validateLeaveDay = async () => {
        const leaveDays : LeaveDay[] = []
        if (isLongLeave && endDate.isAfter(startDate)) {
            // Add all days between startDate and endDate
            let tempDate = startDate
            const daysToAdd = endDate.diff(startDate, 'day')
            for (let i = 0; i <= daysToAdd; i++) {
                leaveDays.push({
                    id: -1,
                    weekNumber: getWeekNumber(startDate.toDate()),
                    year: tempDate.year(),
                    month: tempDate.month(),
                    dayNumber: tempDate.date(),
                    employeeId: EmployeeLeaveDayProps.baseEmployee.id
                })
                tempDate = tempDate.add(1, 'day')
            }
        } else if (!isLongLeave) {
            // Add only startDate
            leaveDays.push({
                id: -1,
                weekNumber: getWeekNumber(startDate.toDate()),
                year: startDate.year(),
                month: startDate.month(),
                dayNumber: startDate.date(),
                employeeId: EmployeeLeaveDayProps.baseEmployee.id
            })
        }else{
            appContext.showSnackbar("La date de fin doit être après la date de début.", "error")
            return
        }
        const res = await addLeaveDays(leaveDays)
        if (res && res.code === 200 && res.data) {
            setEmployeeLeaveDays(res.data);
            appContext.showSnackbar("Congé ajouté avec succès.", "success")
        }else{
            appContext.showSnackbar("Erreur lors de l'ajout du congé.", "error")
        }
    }


    return (
        <div className=''>
            <h1 className='bg-black text-white p-8 sm:p-6 md:p-4 font-bold'>Calendrier des congés de {EmployeeLeaveDayProps.baseEmployee.lastName.toUpperCase()} {EmployeeLeaveDayProps.baseEmployee.firstName}</h1>
            <div className='flex flex-row gap-4 my-4'>
                <div className='border-r-2 border-black border-solid'>
                    <DateCalendar
                    
                        readOnly={true}
                        slots={{
                            day: ServerDay,
                        }}
                        onMonthChange={(date) => {
                            setCurrentMonth(date.month())
                            const monthlyLeaveDays = employeeLeaveDays.filter((leaveDay) => {
                                return date.month() === leaveDay.month
                            }).map((leaveDay) => {
                                return leaveDay.dayNumber
                            })
                            setHighlightedDays(monthlyLeaveDays)
                        }}
                        slotProps={{
                            day: {
                                highlightedDays,
                                employeeId: EmployeeLeaveDayProps.baseEmployee.id,
                                setEmployeeLeaveDays: setEmployeeLeaveDays
                            } as any
                        }}
                    />
                </div>
                <div className='flex flex-col gap-4 justify-items-end px-4'>
                    <h1 className=' p-4 sm:p-6 md:p-2 font-bold'>Poser un congé</h1>
                    <DatePicker
                        label={isLongLeave?"Début":"Date de congé"}
                        value={startDate}
                        onChange={(date) => {
                            setStartDate(dayjs(date))
                            if( isLongLeave ) setEndDate(startDate.add(1, 'day'))
                        }}
                        format="LL"
                    />
                    
                    <div>
                        <span>
                            Congé longue durée
                        </span>
                        <Checkbox 
                        checked={isLongLeave}
                        onChange={()=>{
                            setIsLongLeave(!isLongLeave)
                        }}
                        />
                    </div>
                    {
                        isLongLeave?
                        <DatePicker
                            minDate={startDate.add(1, 'day')}
                            label="Fin"
                            value={endDate}
                            format="LL"
                            onChange={(date) => {
                                setEndDate(dayjs(date))
                            }}
                        />
                        :
                        <div></div>
                    }
                    <Button variant="contained" color={'success'} onClick={validateLeaveDay} >
                        Valider
                    </Button>
                </div>

            
            </div>
            <div className='flex justify-between p-6'>
                <Button onClick={EmployeeLeaveDayProps.closeDialog} color='error' >Fermer</Button>

            </div>
        </div>
    );
}

export default EmployeeLeaveDay;
